/**
 * 保存後の「変更履歴の登録に失敗しました」警告を出すフック (マスタ変更履歴 SBI 8)。
 *
 * 使い方:
 *   const { historyWarning, reportHistory } = useHistoryWarning();
 *   const res = await apiClient.update(...);
 *   reportHistory(res);          // 失敗していれば警告を出す (成功 / 未定義なら何もしない)
 *   ...
 *   {historyWarning}             // 画面側の JSX のどこかに 1 回だけ置く
 *
 * - 既存の `StatusBanner` (variant="warning") を再利用する。保存成功の表示とは別に出す。
 * - 「×」で閉じるまで消えない。次の保存が成功しても消さない (消えると見逃すため)。
 *   次の保存でも失敗したときは、新しい内容に置き換える。
 * - 作成 / 編集モーダルは保存成功で閉じるため、このフックは**モーダルではなく画面側**で使う。
 */
import { useCallback, useMemo, useState } from 'react';
import type React from 'react';

import type { HistoryRecordResult } from '../api/types';
import { StatusBanner } from '../components/StatusBanner';
import { buildHistoryWarning } from '../utils/historyWarning';

export interface UseHistoryWarningResult {
  /** 警告バナー (警告が無いときは null)。画面の JSX に置く */
  historyWarning: React.ReactNode;
  /**
   * 保存系 API のレスポンスを渡す。複数回の呼び出し結果はまとめて渡せる。
   * 失敗が含まれていれば警告を表示し、そうでなければ何もしない。
   */
  reportHistory: (...results: Array<HistoryRecordResult | null | undefined>) => void;
}

export function useHistoryWarning(testId = 'history-warning'): UseHistoryWarningResult {
  const [message, setMessage] = useState<string | null>(null);

  const reportHistory = useCallback(
    (...results: Array<HistoryRecordResult | null | undefined>): void => {
      const warning = buildHistoryWarning(results);
      if (warning !== null) setMessage(warning);
    },
    [],
  );

  const historyWarning = useMemo(
    () =>
      message !== null ? (
        <StatusBanner variant="warning" onDismiss={() => setMessage(null)} data-testid={testId}>
          {message}
        </StatusBanner>
      ) : null,
    [message, testId],
  );

  return { historyWarning, reportHistory };
}
